"use client";

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/auth/AuthProvider";
import { useUnpublishShowcaseItem, type ShowcaseItem } from "@/hooks/useShowcaseApi";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { usePreviewAwareAdmin } from "@/lib/home/planPreview";

type Props = {
  item: Pick<ShowcaseItem, "id" | "user_id" | "title" | "creator_handle">;
  /** "overlay" = icon button for the gallery card; "inline" = labelled button for the detail page. */
  variant?: "overlay" | "inline";
  /** Navigate here after a successful delete (e.g. back to the gallery from a detail page). */
  redirectTo?: string;
  className?: string;
};

/**
 * Delete control for a showcase item. Visible only to users who are allowed to
 * remove it: admins (any item) or the original uploader (their own item).
 * Server-side `unpublishShowcaseItem` enforces the same rule — this is just the
 * matching UI affordance.
 */
export function ShowcaseDeleteButton({ item, variant = "overlay", redirectTo, className }: Props) {
  const { user } = useAuth();
  const { displayIsAdmin: isAdmin } = usePreviewAwareAdmin();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const unpublish = useUnpublishShowcaseItem();

  const isOwner = Boolean(user?.id && user.id === item.user_id);
  const canDelete = isAdmin || isOwner;
  if (!canDelete) return null;

  const handleDelete = async (event: React.MouseEvent) => {
    // Keep the dialog open while the request is in flight (Radix would otherwise
    // close it immediately on click).
    event.preventDefault();
    try {
      await unpublish.mutateAsync({ id: item.id });
      toast.success("Removed from the Showcase");
      setOpen(false);
      if (redirectTo) navigate(redirectTo);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't delete this item.");
    }
  };

  const pending = unpublish.isPending;

  return (
    <AlertDialog open={open} onOpenChange={(next) => !pending && setOpen(next)}>
      <AlertDialogTrigger asChild>
        {variant === "overlay" ? (
          <button
            type="button"
            aria-label="Delete item"
            // Stop the click from bubbling to the card's surrounding link.
            onClick={(e) => e.stopPropagation()}
            className={cn(
              // Kept subtly visible (not hover-only) so it stays reachable on touch
              // devices; only privileged users ever see it at all.
              "absolute right-2 top-2 z-10 grid h-8 w-8 place-items-center rounded-full border border-white/20 bg-black/55 text-white/90 opacity-80 backdrop-blur-sm transition hover:bg-destructive/80 hover:text-destructive-foreground focus-visible:opacity-100 group-hover:opacity-100",
              className,
            )}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-card/70 px-3.5 py-2 text-sm text-foreground/80 transition hover:border-destructive/50 hover:text-destructive",
              className,
            )}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this item?</AlertDialogTitle>
          <AlertDialogDescription>
            {isOwner
              ? "This removes your published work from the Community Showcase. This can’t be undone."
              : `This permanently removes “${item.title}” by ${item.creator_handle} from the Showcase. This can’t be undone.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={pending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {pending ? (
              <span className="inline-flex items-center gap-1.5">
                <Loader2 className="h-4 w-4 animate-spin" /> Deleting…
              </span>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
